import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Loader2, Users, Home, CheckCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface ClientProfile {
  user_id: string;
  phone: string | null;
  desired_bedrooms: number | null;
  desired_bathrooms: number | null;
  max_rent: number | null;
  preferred_cities: string | null;
  preferred_states: string | null;
  additional_requirements: string | null;
}

interface Property {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  bedrooms: number;
  bathrooms: number;
  rent_amount: number;
  status: string;
  description: string | null;
}

interface ClientWithMatches {
  profile: ClientProfile;
  email: string;
  matches: Property[];
  matchScore: number;
}

const ClientMatching = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [clientsWithMatches, setClientsWithMatches] = useState<ClientWithMatches[]>([]);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchClientsAndMatches();
    }
  }, [isAdmin]);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        navigate("/auth");
        return;
      }

      setUser(session.user);

      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (!roleData) {
        toast.error("Access denied. Admin privileges required.");
        navigate("/dashboard");
        return;
      }

      setIsAdmin(true);
    } catch (error: any) {
      console.error("Error checking auth:", error);
      toast.error("Authentication error");
    } finally {
      setLoading(false);
    }
  };

  const fetchClientsAndMatches = async () => {
    try {
      // Fetch all client profiles with preferences
      const { data: profiles, error: profilesError } = await supabase
        .from("client_profiles")
        .select("*");

      if (profilesError) throw profilesError;

      // Fetch all available properties
      const { data: properties, error: propertiesError } = await supabase
        .from("properties")
        .select("*")
        .eq("status", "available");

      if (propertiesError) throw propertiesError;

      // Get user emails
      const { data: userProfiles, error: userError } = await supabase
        .from("profiles")
        .select("id, email");

      if (userError) throw userError;

      const userEmailMap = new Map(userProfiles?.map(u => [u.id, u.email]) || []);

      // Match clients with properties
      const matches: ClientWithMatches[] = (profiles || [])
        .filter(profile => 
          profile.desired_bedrooms !== null || 
          profile.max_rent !== null || 
          profile.preferred_cities !== null
        )
        .map(profile => {
          const matchedProperties = (properties || []).filter(property => {
            let score = 0;

            // Bedroom match
            if (profile.desired_bedrooms !== null) {
              if (property.bedrooms === profile.desired_bedrooms) score += 3;
              else if (Math.abs(property.bedrooms - profile.desired_bedrooms) === 1) score += 1;
              else return false;
            }

            // Bathroom match
            if (profile.desired_bathrooms !== null) {
              if (property.bathrooms >= profile.desired_bathrooms) score += 2;
            }

            // Rent match
            if (profile.max_rent !== null) {
              if (property.rent_amount <= profile.max_rent) score += 3;
              else return false;
            }

            // City match
            if (profile.preferred_cities) {
              const cities = profile.preferred_cities.split(',').map(c => c.trim().toLowerCase());
              if (cities.includes(property.city.toLowerCase())) score += 2;
            }

            // State match
            if (profile.preferred_states) {
              const states = profile.preferred_states.split(',').map(s => s.trim().toLowerCase());
              if (states.includes(property.state.toLowerCase())) score += 1;
            }

            return score > 0;
          });

          const totalScore = matchedProperties.length > 0 ? 
            matchedProperties.reduce((sum, prop) => {
              let score = 5;
              if (profile.desired_bedrooms && prop.bedrooms === profile.desired_bedrooms) score += 3;
              if (profile.max_rent && prop.rent_amount <= profile.max_rent) score += 2;
              return sum + score;
            }, 0) / matchedProperties.length : 0;

          return {
            profile,
            email: userEmailMap.get(profile.user_id) || "Unknown",
            matches: matchedProperties.sort((a, b) => {
              const scoreA = calculatePropertyScore(a, profile);
              const scoreB = calculatePropertyScore(b, profile);
              return scoreB - scoreA;
            }),
            matchScore: totalScore,
          };
        })
        .sort((a, b) => b.matchScore - a.matchScore);

      setClientsWithMatches(matches);
    } catch (error: any) {
      console.error("Error fetching clients and matches:", error);
      toast.error("Failed to load client matches");
    }
  };

  const calculatePropertyScore = (property: Property, profile: ClientProfile): number => {
    let score = 0;
    if (profile.desired_bedrooms && property.bedrooms === profile.desired_bedrooms) score += 3;
    if (profile.desired_bathrooms && property.bathrooms >= profile.desired_bathrooms) score += 2;
    if (profile.max_rent && property.rent_amount <= profile.max_rent) score += 3;
    if (profile.preferred_cities) {
      const cities = profile.preferred_cities.split(',').map(c => c.trim().toLowerCase());
      if (cities.includes(property.city.toLowerCase())) score += 2;
    }
    return score;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-1 container mx-auto px-4 py-8 mt-20">
        <div className="max-w-7xl mx-auto space-y-8">
          <div>
            <h1 className="text-4xl font-bold flex items-center gap-2">
              <Users className="h-8 w-8" />
              Client Matching
            </h1>
            <p className="text-muted-foreground mt-2">
              AI-powered property matching based on client preferences
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Client Matches</CardTitle>
              <CardDescription>
                {clientsWithMatches.length} client{clientsWithMatches.length !== 1 ? "s" : ""} with property matches
              </CardDescription>
            </CardHeader>
            <CardContent>
              {clientsWithMatches.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No clients with preferences found</p>
                </div>
              ) : (
                <Accordion type="single" collapsible className="w-full">
                  {clientsWithMatches.map((client, index) => (
                    <AccordionItem key={client.profile.user_id} value={`client-${index}`}>
                      <AccordionTrigger>
                        <div className="flex items-center gap-4 w-full">
                          <span className="font-medium">{client.email}</span>
                          <Badge variant="secondary">
                            {client.matches.length} match{client.matches.length !== 1 ? "es" : ""}
                          </Badge>
                          <Badge variant="outline">
                            Score: {client.matchScore.toFixed(1)}
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 pt-4">
                          <div className="bg-muted p-4 rounded-lg">
                            <h4 className="font-semibold mb-2">Client Preferences</h4>
                            <div className="grid gap-2 text-sm">
                              {client.profile.desired_bedrooms && (
                                <p>Bedrooms: {client.profile.desired_bedrooms}</p>
                              )}
                              {client.profile.desired_bathrooms && (
                                <p>Bathrooms: {client.profile.desired_bathrooms}</p>
                              )}
                              {client.profile.max_rent && (
                                <p>Max Rent: ${client.profile.max_rent.toLocaleString()}</p>
                              )}
                              {client.profile.preferred_cities && (
                                <p>Preferred Cities: {client.profile.preferred_cities}</p>
                              )}
                              {client.profile.preferred_states && (
                                <p>Preferred States: {client.profile.preferred_states}</p>
                              )}
                            </div>
                          </div>

                          {client.matches.length > 0 ? (
                            <div className="grid gap-4 md:grid-cols-2">
                              {client.matches.map((property) => (
                                <Card key={property.id}>
                                  <CardContent className="p-4 space-y-2">
                                    <div className="flex items-start justify-between">
                                      <h4 className="font-semibold">{property.name}</h4>
                                      <CheckCircle className="h-5 w-5 text-green-600" />
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                      {property.address}, {property.city}, {property.state}
                                    </p>
                                    <div className="flex items-center gap-4 text-sm">
                                      <span>{property.bedrooms} bed</span>
                                      <span>{property.bathrooms} bath</span>
                                      <span className="font-semibold text-primary">
                                        ${property.rent_amount.toLocaleString()}/mo
                                      </span>
                                    </div>
                                    <Badge variant="default" className="w-fit">
                                      Match Score: {calculatePropertyScore(property, client.profile)}
                                    </Badge>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-8">
                              <Home className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                              <p className="text-sm text-muted-foreground">No matching properties found</p>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ClientMatching;
